import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

/**
 * Publishes the page header's action slot to the pages below it.
 *
 * The obvious implementation — `document.getElementById("topbar-actions-slot")`
 * in an effect — races the first mount: on the very first render of the shell
 * the slot is not in the DOM yet, so the first page to mount finds nothing and
 * renders its actions inline. Handing the element down through context instead
 * means the portal target arrives by the same mechanism as everything else, and
 * the element is published by a ref callback rather than looked up.
 */
interface TopbarSlotValue {
  slot: HTMLElement | null;
  setSlot: (element: HTMLElement | null) => void;
}

const TopbarSlotContext = createContext<TopbarSlotValue>({ slot: null, setSlot: () => {} });

export function TopbarSlotProvider({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const value = useMemo(() => ({ slot, setSlot }), [slot]);
  return <TopbarSlotContext.Provider value={value}>{children}</TopbarSlotContext.Provider>;
}

export function useTopbarSlot(): TopbarSlotValue {
  return useContext(TopbarSlotContext);
}
